/**
 * Commission Helper Functions
 *
 * Handles commission data operations for the RFP broker.commissions[] array:
 * - Transforming between API and grid formats
 * - Finding commission entries by coverage and plan
 * - Upserting commission data into the array
 * - Building complete commission update requests
 *
 * Commission amounts use different API fields based on type:
 * - percentage, pepm, net, standard, graded → use amount field (number)
 * - heaped → use heapedamount field (array of numbers)
 *
 * Frontend represents heaped as { first, thereafter } object
 * API represents heaped as array [first, thereafter, ...]
 */

import {
  heapedToPair,
  pairToHeaped,
  shouldOmitAmount,
} from "@platform/formatting-utils";

export { shouldOmitAmount };

/**
 * Heaped commission value with first year and thereafter percentages
 */
export interface HeapedCommissionValue {
  first: number;
  thereafter: number;
}

/**
 * Commission data from API
 */
export interface ApiCommission {
  coverageType?: string;
  benefitPlanIdentifier?: string;
  /** UUID from backend (benefitPlanId in the GET response) */
  benefitPlanId?: string;
  currentType?: string;
  currentAmount?: number;
  currentHeapedAmount?: number[];
  requestedType?: string;
  requestedAmount?: number;
  requestedHeapedAmount?: number[];
}

/**
 * Commission data for frontend grid
 */
export interface GridCommission {
  currentCommissionType?: string;
  currentCommissionAmount?: number | HeapedCommissionValue;
  requestedCommissionType?: string;
  requestedCommissionAmount?: number | HeapedCommissionValue;
}

/**
 * Transforms commission data from API format to frontend grid format
 *
 * Converts API commission entry into flat fields for the grid.
 * Handles heaped commission conversion from array to object.
 *
 * @param commission - API commission entry (may be undefined)
 * @returns Object with flat commission fields for grid
 */
export const transformCommissionsFromApi = (
  commission?: ApiCommission,
): GridCommission => {
  if (!commission) {
    return {};
  }

  const result: GridCommission = {};

  // Current commission
  if (commission.currentType) {
    result.currentCommissionType = commission.currentType;

    if (commission.currentType === "heaped" && commission.currentHeapedAmount) {
      const heaped = pairToHeaped(commission.currentHeapedAmount);
      if (heaped) {
        result.currentCommissionAmount = heaped;
      }
    } else if (commission.currentAmount !== undefined) {
      result.currentCommissionAmount = commission.currentAmount;
    }
  }

  // Requested commission
  if (commission.requestedType) {
    result.requestedCommissionType = commission.requestedType;

    if (
      commission.requestedType === "heaped" &&
      commission.requestedHeapedAmount
    ) {
      const heaped = pairToHeaped(commission.requestedHeapedAmount);
      if (heaped) {
        result.requestedCommissionAmount = heaped;
      }
    } else if (commission.requestedAmount !== undefined) {
      result.requestedCommissionAmount = commission.requestedAmount;
    }
  }

  return result;
};

/**
 * Checks if a commission entry matches the given coverage and benefit plan
 *
 * @param commission - Commission entry to check
 * @param coverageType - Coverage type to match
 * @param benefitPlanIdentifier - Benefit plan identifier to match
 * @returns True if the commission matches
 */
const matchesCommission = (
  commission: ApiCommission,
  coverageType: string,
  benefitPlanIdentifier: string,
): boolean =>
  commission.coverageType === coverageType &&
  (commission.benefitPlanIdentifier === benefitPlanIdentifier ||
    commission.benefitPlanId === benefitPlanIdentifier);

/**
 * Transforms commission data from frontend grid format to API format
 *
 * Converts flat grid fields into API commission entry structure.
 * Handles heaped commission conversion from object to array.
 * Strips amounts for types that don't support them (net, standard, notApplicable).
 *
 * @param currentType - Current commission type
 * @param currentAmount - Current commission amount (number or heaped object)
 * @param requestedType - Requested commission type
 * @param requestedAmount - Requested commission amount (number or heaped object)
 * @returns API commission entry, or null if no commission data
 */
export const transformCommissionsToApi = (
  currentType?: string,
  currentAmount?: number | { first: number; thereafter: number },
  requestedType?: string,
  requestedAmount?: number | { first: number; thereafter: number },
): Partial<ApiCommission> | null => {
  const hasCurrentData = currentType !== undefined;
  const hasRequestedData = requestedType !== undefined;

  if (!hasCurrentData && !hasRequestedData) {
    return null;
  }

  const result: Partial<ApiCommission> = {};

  if (currentType) {
    const { amount, heapedAmount } = commissionAmountsToApi(
      currentType,
      currentAmount,
    );
    result.currentType = currentType;
    if (amount !== undefined) result.currentAmount = amount;
    if (heapedAmount) result.currentHeapedAmount = heapedAmount;
  }

  if (requestedType) {
    const { amount, heapedAmount } = commissionAmountsToApi(
      requestedType,
      requestedAmount,
    );
    result.requestedType = requestedType;
    if (amount !== undefined) result.requestedAmount = amount;
    if (heapedAmount) result.requestedHeapedAmount = heapedAmount;
  }

  return result;
};

/** The amount fields a commission type carries, before they are named per side. */
interface CommissionAmounts {
  amount?: number;
  heapedAmount?: number[];
}

/**
 * Resolves which amount a commission type carries. Heaped carries its two
 * percentages in heapedAmount and never in amount; the types that take no
 * detail carry neither.
 */
const commissionAmountsToApi = (
  type: string,
  amount?: number | HeapedCommissionValue,
): CommissionAmounts => {
  if (type === "heaped") {
    const pair = typeof amount === "object" ? heapedToPair(amount) : null;

    return pair ? { heapedAmount: pair } : {};
  }

  return !shouldOmitAmount(type) && typeof amount === "number"
    ? { amount }
    : {};
};

/**
 * Extracts commission fields for a specific coverage from broker.commissions[] array
 *
 * Finds the matching commission entry and transforms it to grid format.
 *
 * @param commissions - Array of commission entries from broker.commissions
 * @param coverageType - Coverage type to match
 * @param benefitPlanIdentifier - Benefit plan identifier to match
 * @returns Flat commission fields for grid, or empty object if not found
 */
export const extractCommissionsForCoverage = (
  commissions: ApiCommission[] | undefined,
  coverageType: string,
  benefitPlanIdentifier: string,
): GridCommission => {
  if (!commissions || !Array.isArray(commissions)) {
    return {};
  }

  const match = commissions.find((c) =>
    matchesCommission(c, coverageType, benefitPlanIdentifier),
  );

  return transformCommissionsFromApi(match);
};

/**
 * Upserts commission data for a specific coverage into broker.commissions[] array
 *
 * Finds existing entry or creates new one, updates with transformed data.
 * Returns updated array.
 *
 * @param commissions - Current broker.commissions array
 * @param coverageType - Coverage type
 * @param benefitPlanIdentifier - Benefit plan identifier
 * @param currentType - Current commission type from grid
 * @param currentAmount - Current commission amount from grid
 * @param requestedType - Requested commission type from grid
 * @param requestedAmount - Requested commission amount from grid
 * @returns Updated commissions array
 */
export const upsertCommissionsForCoverage = (
  commissions: ApiCommission[] | undefined,
  coverageType: string,
  benefitPlanIdentifier: string,
  currentType?: string,
  currentAmount?: number | { first: number; thereafter: number },
  requestedType?: string,
  requestedAmount?: number | { first: number; thereafter: number },
): ApiCommission[] => {
  const commissionsArray = commissions || [];

  // Transform grid fields to API format
  const commissionData = transformCommissionsToApi(
    currentType,
    currentAmount,
    requestedType,
    requestedAmount,
  );

  // If no commission data, remove existing entry if present
  if (!commissionData) {
    return commissionsArray.filter(
      (c) => !matchesCommission(c, coverageType, benefitPlanIdentifier),
    );
  }

  // Find existing entry
  const existingIndex = commissionsArray.findIndex((c) =>
    matchesCommission(c, coverageType, benefitPlanIdentifier),
  );

  const updatedEntry: ApiCommission = {
    coverageType,
    benefitPlanIdentifier,
    ...commissionData,
  };

  // Update or insert
  if (existingIndex >= 0) {
    // Update existing
    const updated = [...commissionsArray];
    updated[existingIndex] = updatedEntry;
    return updated;
  }
  // Insert new
  return [...commissionsArray, updatedEntry];
};
